// src/services/apiService.js
class ApiService {
  constructor(baseURL, defaultOptions = {}) {
    this.baseURL = baseURL || process.env.REACT_APP_API_URL || 'https://holzflex-api-588409326737.us-central1.run.app';
    this.defaultOptions = {
      maxRetries: 2,      // try once, wait, retry once
      retryDelay: 3000,   // 3s fixed wait
      ...defaultOptions
    };

    // Track rate limit state
    this.rateLimitState = {
      isRateLimited: false,
      resetTime: null,
      requestCount: 0,
      windowStart: Date.now()
    };
  }

  // Check if we should make a request based on rate limit state
  shouldMakeRequest() {
    const now = Date.now();

    // Reset rate limit tracking every minute
    if (now - this.rateLimitState.windowStart > 60000) {
      this.rateLimitState = {
        isRateLimited: false,
        resetTime: null,
        requestCount: 0,
        windowStart: now
      };
    }

    // If we're rate limited and reset time hasn't passed, don't make request
    if (this.rateLimitState.isRateLimited && this.rateLimitState.resetTime > now) {
      return false;
    }

    return true;
  }

  async request(endpoint, options = {}) {
    const { maxRetries, retryDelay, ...fetchOptions } = {
      ...this.defaultOptions,
      ...options
    };

    const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;

    // Check rate limit before making request
    if (!this.shouldMakeRequest()) {
      const waitTime = Math.ceil((this.rateLimitState.resetTime - Date.now()) / 1000);
      throw new Error(`Rate limited. Please wait ${waitTime} seconds before trying again.`);
    }

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        this.rateLimitState.requestCount++;

        const response = await fetch(url, fetchOptions);

        const responseText = await response.text();
        let responseData;

        try {
          responseData = JSON.parse(responseText);
        } catch {
          responseData = responseText;
        }

        if (!response.ok) {
          const serverMessage = responseData && typeof responseData === 'object' ? responseData.error : null;
          const error = new Error(serverMessage || `HTTP ${response.status}: ${response.statusText || response.status}`);
          error.status = response.status;
          error.statusCode = response.status;
          error.code = responseData && typeof responseData === 'object' ? responseData.code : undefined;
          error.responseData = responseData;

          const isRateLimit = response.status === 429;
          const isRetryable = isRateLimit || response.status === 503 || response.status === 500;

          if (isRateLimit) {
            const retryAfter = response.headers.get('Retry-After');
            const resetTime = retryAfter ?
              Date.now() + (parseInt(retryAfter) * 1000) :
              Date.now() + 60000;
            this.rateLimitState.isRateLimited = true;
            this.rateLimitState.resetTime = resetTime;
            error.isRateLimit = true;
            error.code = 'RATE_LIMIT_EXCEEDED';
          }

          if (isRetryable && attempt < maxRetries) {
            const delay = isRateLimit
              ? 10000 + Math.random() * 2000
              : retryDelay + Math.random() * 1000;
            console.log(`🔄 HTTP ${response.status} on attempt ${attempt}, retrying in ${Math.round(delay / 1000)}s`);
            await new Promise(resolve => setTimeout(resolve, delay));
            continue;
          }

          if (isRateLimit) {
            error.message = `Too many requests. Please wait a moment before trying again.`;
          }

          throw error;
        }

        // Success - reset rate limit state if we were limited
        if (this.rateLimitState.isRateLimited) {
          this.rateLimitState.isRateLimited = false;
          this.rateLimitState.resetTime = null;
        }

        if (attempt > 1) {
          console.log(`✅ Connection established after ${attempt} attempts`);
        }
        return responseData;

      } catch (error) {
        const isLastAttempt = attempt === maxRetries;
        const isNetworkError = error.message?.includes('Failed to fetch') ||
                               error.message?.includes('NetworkError');

        if (isLastAttempt || (!isNetworkError && !error.isRateLimit)) {
          // An aborted request (caller navigated away / a polling cycle
          // canceled it) is expected cancellation, not a failure — don't
          // log it as one.
          if (!error.status && error.name !== 'AbortError') {
            console.error(`❌ API call to ${endpoint} failed:`, error.message);
          }
          throw error;
        }

        const delay = error.isRateLimit
          ? 10000 + Math.random() * 2000
          : retryDelay + Math.random() * 1000;
        console.log(`🔄 Network error on attempt ${attempt}, retrying in ${Math.round(delay / 1000)}s`);
        await new Promise(resolve => setTimeout(resolve, delay));
      }
    }
  }

  // Helper method to get auth headers
  async getAuthHeaders(currentUser) {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }

    const token = await currentUser.getIdToken();
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  }

  // Convenience methods with auth support
  async get(endpoint, currentUser, options = {}) {
    const headers = await this.getAuthHeaders(currentUser);
    return this.request(endpoint, {
      ...options,
      method: 'GET',
      headers: { ...headers, ...options.headers }
    });
  }

  async post(endpoint, data, currentUser, options = {}) {
    const headers = await this.getAuthHeaders(currentUser);
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      headers: { ...headers, ...options.headers },
      body: JSON.stringify(data)
    });
  }

  // Upload with FormData - must NOT set Content-Type, the browser attaches
  // the multipart boundary automatically when given a raw FormData body.
  async postFormData(endpoint, formData, currentUser, options = {}) {
    if (!currentUser) {
      throw new Error('User not authenticated');
    }
    const token = await currentUser.getIdToken();
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}`, ...options.headers },
      body: formData
    });
  }

  async put(endpoint, data, currentUser, options = {}) {
    const headers = await this.getAuthHeaders(currentUser);
    return this.request(endpoint, {
      ...options,
      method: 'PUT',
      headers: { ...headers, ...options.headers },
      body: JSON.stringify(data)
    });
  }

  async patch(endpoint, data, currentUser, options = {}) {
    const headers = await this.getAuthHeaders(currentUser);
    return this.request(endpoint, {
      ...options,
      method: 'PATCH',
      headers: { ...headers, ...options.headers },
      body: JSON.stringify(data)
    });
  }

  async delete(endpoint, currentUser, options = {}) {
    const headers = await this.getAuthHeaders(currentUser);
    return this.request(endpoint, {
      ...options,
      method: 'DELETE',
      headers: { ...headers, ...options.headers }
    });
  }

  // Methods without auth (for public endpoints)
  async publicPost(endpoint, data, options = {}) {
    return this.request(endpoint, {
      ...options,
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...options.headers
      },
      body: JSON.stringify(data)
    });
  }

  // Simple polling method - NO RETRIES, just returns status
  // Use this for polling endpoints where you want full control over retry logic
  async pollStatus(endpoint, currentUser) {
    try {
      const token = await currentUser.getIdToken();
      const url = endpoint.startsWith('http') ? endpoint : `${this.baseURL}${endpoint}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      // Parse response body
      let data = null;
      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          data = await response.json();
        } catch (e) {
          console.error('Failed to parse JSON response:', e);
        }
      }

      // Return structured response - NO THROWING
      return {
        ok: response.ok,
        status: response.status,
        statusText: response.statusText,
        data: data,
        isRateLimit: response.status === 429,
        isNotModified: response.status === 304
      };

    } catch (error) {
      // Network errors or auth errors - return structured response
      return {
        ok: false,
        status: 0,
        statusText: 'Network Error',
        data: null,
        error: error.message,
        isNetworkError: true
      };
    }
  }

  // Manual rate limit reset (for admin use)
  resetRateLimit() {
    this.rateLimitState = {
      isRateLimited: false,
      resetTime: null,
      requestCount: 0,
      windowStart: Date.now()
    };
    console.log('🔄 Rate limit state manually reset');
  }
}

// Create and export a singleton instance
const apiService = new ApiService();

export default apiService;
